import type { VisualMapping } from '../../../types/wizardTypes';

interface KeyValuePair {
  key: string;
  value: string;
  selected?: boolean;
  dynamic?: boolean;
}

interface JinjaToken {
  __jinjaTemplate: string;
  __quoted: boolean;
}

/**
 * Convert visual mappings to bodyTemplate string and/or queryMap array.
 */
export function mappingsToTemplate(
  mappings: VisualMapping[],
  httpMethod: string
): { bodyTemplate: string; queryMap: KeyValuePair[] } {
  const queryMappings = mappings.filter(m => m.isQueryParam);
  const bodyMappings = mappings.filter(m => !m.isQueryParam);

  const queryMap: KeyValuePair[] = queryMappings.map(m => ({
    key: m.targetPath,
    value: `{{${m.sourceFieldName}}}`,
    selected: true,
    dynamic: true,
  }));

  let bodyTemplate = '';
  if (httpMethod !== 'GET' && bodyMappings.length > 0) {
    bodyTemplate = buildBodyTemplate(bodyMappings);
  }

  return { bodyTemplate, queryMap };
}

/**
 * Build a Jinja2 expression from a mapping's options.
 * e.g. {{ field | default(true) }}
 */
function buildJinjaExpression(m: VisualMapping): string {
  const parts: string[] = [m.sourceFieldName];
  if (m.defaultValue !== undefined && m.defaultValue !== '') {
    parts.push(`default(${m.defaultValue})`);
  }
  return `{{${parts.join(' | ')}}}`;
}

/**
 * Build a Jinja2 body template from mappings.
 * Supports nested paths, filters, conditionals, and defaults.
 */
function buildBodyTemplate(mappings: VisualMapping[]): string {
  const required = mappings.filter(m => !m.isOptional);
  const optional = mappings.filter(m => m.isOptional);

  const root: Record<string, unknown> = {};
  for (const m of required) {
    setTemplateValueByPath(root, m.targetPath, buildJinjaExpression(m));
  }

  let body = jsonWithJinjaVars(root);

  // Append optional fields as {% if %} blocks before closing brace
  if (optional.length > 0 && body.trimEnd().endsWith('}')) {
    const optionalLines = optional.map(m => {
      const expr = buildJinjaExpression(m);
      const key = m.targetPath;
      return `  {% if ${m.sourceFieldName} %}, "${key}": ${expr}{% endif %}`;
    });
    const lastBrace = body.lastIndexOf('}');
    body = body.slice(0, lastBrace) + '\n' + optionalLines.join('\n') + '\n}';
  }

  return body;
}

/**
 * Serialize object to JSON, emitting Jinja expressions unquoted.
 */
function jsonWithJinjaVars(obj: unknown, indent = 0): string {
  const pad = '  '.repeat(indent);
  const padInner = '  '.repeat(indent + 1);

  if (obj === null || obj === undefined) return 'null';
  if (isJinjaToken(obj)) {
    return obj.__quoted ? JSON.stringify(obj.__jinjaTemplate) : obj.__jinjaTemplate;
  }
  if (typeof obj === 'string') {
    // Jinja expressions are emitted unquoted
    if (/^\{\{.*\}\}$/.test(obj.trim())) {
      return obj;
    }
    return JSON.stringify(obj);
  }
  if (typeof obj === 'number' || typeof obj === 'boolean') return String(obj);

  if (Array.isArray(obj)) {
    if (obj.length === 0) return '[]';
    const items = obj.map(item => `${padInner}${jsonWithJinjaVars(item, indent + 1)}`);
    return `[\n${items.join(',\n')}\n${pad}]`;
  }

  const entries = Object.entries(obj as Record<string, unknown>);
  if (entries.length === 0) return '{}';

  const lines = entries.map(
    ([key, value]) => `${padInner}${JSON.stringify(key)}: ${jsonWithJinjaVars(value, indent + 1)}`
  );
  return `{\n${lines.join(',\n')}\n${pad}}`;
}

/**
 * Parse a Jinja inner expression like "field | default(true) | tojson"
 * into its components.
 */
function parseJinjaInner(inner: string): {
  fieldName: string;
  hasToJson: boolean;
  defaultValue?: string;
} {
  const parts = inner.split('|').map(s => s.trim());
  const fieldName = parts[0];
  let hasToJson = false;
  let defaultValue: string | undefined;

  for (let i = 1; i < parts.length; i++) {
    const part = parts[i];
    if (part === 'tojson') {
      hasToJson = true;
    } else {
      const defaultMatch = part.match(/^default\((.+)\)$/);
      if (defaultMatch) {
        defaultValue = defaultMatch[1];
      }
    }
  }

  return { fieldName, hasToJson, defaultValue };
}

/**
 * Convert bodyTemplate + queryMap back to visual mappings (for edit-mode hydration).
 * Supports Jinja2 filters (tojson), conditionals ({% if %}), and defaults.
 */
export function templateToMappings(
  bodyTemplate: string,
  queryMap: KeyValuePair[] | undefined
): VisualMapping[] {
  const mappings: VisualMapping[] = [];

  if (bodyTemplate) {
    // Phase A: Extract {% if field %}..."key": {{ expr }}{% endif %} blocks
    const conditionalRegex = /\{%\s*if\s+(\w+)\s*%\}[,\s]*"([^"]+)":\s*(\{\{[^}]*\}\})\s*\{%\s*endif\s*%\}/g;
    let cleaned = bodyTemplate;
    let condMatch: RegExpExecArray | null;

    while ((condMatch = conditionalRegex.exec(bodyTemplate)) !== null) {
      const [fullMatch, , key, jinjaExpr] = condMatch;
      // Extract the inner expression from {{ ... }}
      const innerMatch = jinjaExpr.match(/\{\{\s*(.+?)\s*\}\}/);
      if (innerMatch) {
        const parsed = parseJinjaInner(innerMatch[1]);
        mappings.push({
          id: crypto.randomUUID(),
          sourceFieldName: parsed.fieldName,
          targetPath: key,
          isQueryParam: false,
          useToJson: false,
          isOptional: true,
          defaultValue: parsed.defaultValue,
        });
      }
      cleaned = cleaned.replace(fullMatch, '');
    }

    // Phase B: Parse remaining JSON-like body with Jinja expressions
    const varMap = new Map<string, { fieldName: string; hasToJson: boolean; defaultValue?: string }>();
    let counter = 0;
    // Match optional surrounding quotes so "{{TYPE}}" becomes "___JINJA_0__" (not ""__JINJA_0__"")
    const placeholdered = cleaned.replace(
      /"?\{\{([^}]*)\}\}"?/g,
      (_match, inner) => {
        const placeholder = `"__JINJA_${counter}__"`;
        varMap.set(`__JINJA_${counter}__`, parseJinjaInner(inner.trim()));
        counter++;
        return placeholder;
      }
    );

    try {
      const parsed = JSON.parse(placeholdered.replace(/,(\s*[\]}])/g, '$1'));
      collectJinjaMappings(parsed, '', mappings, varMap);
    } catch {
      // Fallback: extract all {{ }} expressions with regex
      const fallbackRegex = /\{\{([^}]+)\}\}/g;
      let fm: RegExpExecArray | null;
      const seen = new Set<string>();
      while ((fm = fallbackRegex.exec(cleaned)) !== null) {
        const parsed = parseJinjaInner(fm[1].trim());
        if (seen.has(parsed.fieldName)) continue;
        seen.add(parsed.fieldName);
        mappings.push({
          id: crypto.randomUUID(),
          sourceFieldName: parsed.fieldName,
          targetPath: parsed.fieldName,
          isQueryParam: false,
          useToJson: false,
          isOptional: false,
          defaultValue: parsed.defaultValue,
        });
      }
    }
  }

  // Extract from queryMap
  if (Array.isArray(queryMap)) {
    for (const pair of queryMap) {
      if (!pair.dynamic) continue;
      const varMatch = pair.value.match(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/);
      const sourceField = varMatch ? varMatch[1] : pair.key;
      mappings.push({
        id: crypto.randomUUID(),
        sourceFieldName: sourceField,
        targetPath: pair.key,
        isQueryParam: true,
        useToJson: false,
        isOptional: false,
      });
    }
  }

  return mappings;
}

/**
 * Collect mappings from a parsed JSON object with Jinja placeholders.
 */
function collectJinjaMappings(
  obj: unknown,
  prefix: string,
  result: VisualMapping[],
  varMap: Map<string, { fieldName: string; hasToJson: boolean; defaultValue?: string }>
): void {
  if (typeof obj === 'string') {
    const match = obj.match(/^__JINJA_(\d+)__$/);
    if (match) {
      const info = varMap.get(`__JINJA_${match[1]}__`);
      if (info) {
        result.push({
          id: crypto.randomUUID(),
          sourceFieldName: info.fieldName,
          targetPath: prefix,
          isQueryParam: false,
          useToJson: false,
          isOptional: false,
          defaultValue: info.defaultValue,
        });
      }
    }
    return;
  }

  if (Array.isArray(obj)) {
    for (let i = 0; i < obj.length; i++) {
      const nextPrefix = prefix ? `${prefix}[${i}]` : `[${i}]`;
      collectJinjaMappings(obj[i], nextPrefix, result, varMap);
    }
    return;
  }

  if (obj && typeof obj === 'object') {
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      collectJinjaMappings(value, prefix ? `${prefix}.${key}` : key, result, varMap);
    }
  }
}

/**
 * Set a value by dot/bracket path.
 */
function setTemplateValueByPath(root: unknown, path: string, value: unknown): void {
  const tokens = tokenizePath(path);
  if (tokens.length === 0) return;

  let current: unknown = root;

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    const next = tokens[i + 1];
    const isLast = i === tokens.length - 1;

    if (typeof token === 'string') {
      if (typeof current !== 'object' || current === null || Array.isArray(current)) return;
      const obj = current as Record<string, unknown>;

      if (isLast) {
        obj[token] = value;
        return;
      }

      if (typeof next === 'number') {
        if (!Array.isArray(obj[token])) obj[token] = [];
      } else if (!obj[token] || typeof obj[token] !== 'object' || Array.isArray(obj[token])) {
        obj[token] = {};
      }

      current = obj[token];
      continue;
    }

    if (!Array.isArray(current)) return;
    const arr = current as unknown[];
    const idx = token;

    while (arr.length <= idx) arr.push(undefined);

    if (isLast) {
      arr[idx] = value;
      return;
    }

    if (typeof next === 'number') {
      if (!Array.isArray(arr[idx])) arr[idx] = [];
    } else if (!arr[idx] || typeof arr[idx] !== 'object' || Array.isArray(arr[idx])) {
      arr[idx] = {};
    }

    current = arr[idx];
  }
}

/**
 * Patch an existing JSON-like body template in-place.
 * Preserves static content while updating, moving, adding, or removing dynamic mappings.
 * Falls back to null for templates that can't be safely parsed.
 */
export function tryPatchBodyTemplate(
  base: string,
  prev: VisualMapping[],
  next: VisualMapping[]
): string | null {
  const parsed = parseJsonLikeTemplate(base);
  if (!parsed) return null;

  const { root, expressions } = parsed;
  const previousExpressions = new Map<string, JinjaToken>();

  for (const mapping of prev) {
    const placeholderKey = getValueByPath(root, mapping.targetPath);
    if (typeof placeholderKey === 'string' && expressions.has(placeholderKey)) {
      previousExpressions.set(mapping.id, expressions.get(placeholderKey)!);
    }
  }

  const nextIds = new Set(next.map(m => m.id));
  for (const mapping of prev) {
    if (nextIds.has(mapping.id)) {
      deleteValueByPath(root, mapping.targetPath);
    } else if (mapping.originalValue !== undefined) {
      // Restore original static value when mapping is removed
      try {
        const restored = JSON.parse(mapping.originalValue);
        setTemplateValueByPath(root, mapping.targetPath, restored);
      } catch {
        setTemplateValueByPath(root, mapping.targetPath, mapping.originalValue);
      }
    } else {
      deleteValueByPath(root, mapping.targetPath);
    }
  }

  for (const mapping of next) {
    const preservedExpression = previousExpressions.get(mapping.id);
    setTemplateValueByPath(root, mapping.targetPath, updateJinjaExpressionSource(preservedExpression, mapping));
  }

  return jsonWithJinjaVars(materializeJinjaExpressions(root, expressions));
}

function parseJsonLikeTemplate(base: string): { root: unknown; expressions: Map<string, JinjaToken> } | null {
  if (!base.trim() || /{%\s*|%}/.test(base)) {
    return null;
  }

  const expressions = new Map<string, JinjaToken>();
  let counter = 0;
  const placeholdered = base.replace(
    /("?)\{\{([^}]*)\}\}\1/g,
    (_match, quote, inner) => {
      const key = `__JINJA_${counter}__`;
      expressions.set(key, {
        __jinjaTemplate: `{{${inner}}}`,
        __quoted: quote === '"',
      });
      counter++;
      return JSON.stringify(key);
    }
  );

  try {
    return {
      root: JSON.parse(placeholdered.replace(/,(\s*[\]}])/g, '$1')),
      expressions,
    };
  } catch {
    return null;
  }
}

function updateJinjaExpressionSource(expression: JinjaToken | undefined, mapping: VisualMapping): JinjaToken {
  if (!expression) {
    return {
      __jinjaTemplate: buildJinjaExpression(mapping),
      __quoted: false,
    };
  }

  const match = expression.__jinjaTemplate.match(/^\{\{\s*([^\s|}]+)([\s\S]*?)\s*\}\}$/);
  if (!match) {
    return {
      __jinjaTemplate: buildJinjaExpression(mapping),
      __quoted: expression.__quoted,
    };
  }

  if (match[1] === mapping.sourceFieldName) {
    return expression;
  }

  const suffix = match[2] || '';
  return {
    __jinjaTemplate: `{{${mapping.sourceFieldName}${suffix}}}`,
    __quoted: expression.__quoted,
  };
}

function materializeJinjaExpressions(
  obj: unknown,
  expressions: Map<string, JinjaToken>
): unknown {
  if (typeof obj === 'string') {
    return expressions.get(obj) ?? obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => materializeJinjaExpressions(item, expressions));
  }

  if (obj && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>).map(([key, value]) => [
        key,
        materializeJinjaExpressions(value, expressions),
      ])
    );
  }

  return obj;
}

function isJinjaToken(value: unknown): value is JinjaToken {
  return (
    !!value &&
    typeof value === 'object' &&
    '__jinjaTemplate' in value &&
    '__quoted' in value
  );
}

function getValueByPath(root: unknown, path: string): unknown {
  const tokens = tokenizePath(path);
  let current = root;

  for (const token of tokens) {
    if (typeof token === 'string') {
      if (!current || typeof current !== 'object' || Array.isArray(current)) return undefined;
      current = (current as Record<string, unknown>)[token];
      continue;
    }

    if (!Array.isArray(current)) return undefined;
    current = current[token];
  }

  return current;
}

function deleteValueByPath(root: unknown, path: string): void {
  const tokens = tokenizePath(path);
  if (tokens.length === 0) return;

  const stack: Array<{ parent: unknown; token: string | number }> = [];
  let current = root;

  for (const token of tokens) {
    if (typeof token === 'string') {
      if (!current || typeof current !== 'object' || Array.isArray(current)) return;
      stack.push({ parent: current, token });
      current = (current as Record<string, unknown>)[token];
      continue;
    }

    if (!Array.isArray(current)) return;
    stack.push({ parent: current, token });
    current = current[token];
  }

  for (let index = stack.length - 1; index >= 0; index--) {
    const { parent, token } = stack[index];

    if (typeof token === 'number') {
      if (!Array.isArray(parent) || token >= parent.length) return;
      parent.splice(token, 1);
      if (parent.length > 0) return;
      continue;
    }

    if (!parent || typeof parent !== 'object' || Array.isArray(parent)) return;
    const record = parent as Record<string, unknown>;
    delete record[token];
    if (Object.keys(record).length > 0) return;
  }
}

function tokenizePath(path: string): Array<string | number> {
  const tokens: Array<string | number> = [];
  const regex = /([^[.\]]+)|\[(\d*)\]/g;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(path)) !== null) {
    if (match[1] !== undefined) {
      tokens.push(match[1]);
    } else {
      tokens.push(match[2] === '' ? 0 : Number(match[2]));
    }
  }

  return tokens;
}
